"use client";

import React, { useEffect, useRef, useState, useCallback } from "react";
import Image from "next/image";

// Mascot images to randomly display
const MASCOT_IMAGES = [
    "/mascots/spriggan.png",
    "/mascots/chocobo.png",
    "/mascots/moogle.png",
    "/mascots/namazu.png",
    "/mascots/cactuar.png",
];

interface Mascot {
    id: number;
    image: string;
    x: number;
    y: number;
    velocityX: number;
    velocityY: number;
    sizePercent: number; // Size as percentage of container diagonal
    rotation: number;
    rotationSpeed: number;
    opacity: number;
    fadeIn: boolean;
    fadeOut: boolean;
    lifetime: number;
    maxLifetime: number;
}

/**
 * MascotOverlay - Displays multiple mascot images popping up and moving around
 * to block the view in the 360 photosphere viewer
 */
const MascotOverlay: React.FC = () => {
    const [mascots, setMascots] = useState<Mascot[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const containerSizeRef = useRef({ width: 1000, height: 800 });
    const animationFrameRef = useRef<number | null>(null);
    const mascotIdCounter = useRef(0);
    const lastSpawnTimeRef = useRef(0);

    // Configuration
    const MAX_MASCOTS = 100;
    const SPAWN_INTERVAL = 10; // ms between spawns
    const MIN_LIFETIME = 2000; // ms
    const MAX_LIFETIME = 5000; // ms
    const MIN_SIZE_PERCENT = 4; // Minimum size as % of container diagonal
    const MAX_SIZE_PERCENT = 20; // Maximum size as % of container diagonal
    const MIN_SPEED = 0.5;
    const MAX_SPEED = 1.0;
    const FADE_DURATION = 300; // ms for fade in/out

    // Calculate pixel size from percentage based on container diagonal
    const getPixelSize = useCallback((sizePercent: number): number => {
        const { width, height } = containerSizeRef.current;
        const diagonal = Math.sqrt(width * width + height * height);
        return (sizePercent / 100) * diagonal;
    }, []);

    // Update container size
    const updateContainerSize = useCallback(() => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            containerSizeRef.current = { width: rect.width, height: rect.height };
        }
    }, []);

    // Create a new mascot with random properties
    const createMascot = useCallback((): Mascot => {
        const id = mascotIdCounter.current++;
        const image = MASCOT_IMAGES[Math.floor(Math.random() * MASCOT_IMAGES.length)];
        const sizePercent = MIN_SIZE_PERCENT + Math.random() * (MAX_SIZE_PERCENT - MIN_SIZE_PERCENT);
        const maxLifetime = MIN_LIFETIME + Math.random() * (MAX_LIFETIME - MIN_LIFETIME);

        // Random position anywhere on screen
        const x = Math.random() * 100;
        const y = Math.random() * 100;

        // Random velocity in any direction
        const angle = Math.random() * Math.PI * 2;
        const speed = MIN_SPEED + Math.random() * (MAX_SPEED - MIN_SPEED);
        const velocityX = Math.cos(angle) * speed;
        const velocityY = Math.sin(angle) * speed;

        // Random rotation
        const rotation = Math.random() * 360;
        const rotationSpeed = (Math.random() - 0.5) * 8; // -4 to 4 degrees per frame

        return {
            id,
            image,
            x,
            y,
            velocityX,
            velocityY,
            sizePercent,
            rotation,
            rotationSpeed,
            opacity: 0,
            fadeIn: true,
            fadeOut: false,
            lifetime: 0,
            maxLifetime,
        };
    }, []);

    // Handle resize
    useEffect(() => {
        updateContainerSize();
        window.addEventListener("resize", updateContainerSize);
        return () => window.removeEventListener("resize", updateContainerSize);
    }, [updateContainerSize]);

    // Animation loop
    useEffect(() => {
        let lastTime = performance.now();

        const animate = (currentTime: number) => {
            const deltaTime = currentTime - lastTime;
            lastTime = currentTime;

            // Update container size each frame for smooth responsiveness
            updateContainerSize();

            setMascots((prevMascots) => {
                let newMascots = [...prevMascots];

                // Spawn new mascots periodically
                if (
                    currentTime - lastSpawnTimeRef.current > SPAWN_INTERVAL &&
                    newMascots.length < MAX_MASCOTS
                ) {
                    newMascots.push(createMascot());
                    lastSpawnTimeRef.current = currentTime;
                }

                // Update each mascot
                newMascots = newMascots
                    .map((mascot) => {
                        let { x, y, velocityX, velocityY, rotation, opacity, fadeIn, fadeOut, lifetime } = mascot;
                        const { rotationSpeed, maxLifetime } = mascot;

                        // Update lifetime
                        lifetime += deltaTime;

                        // Handle fade in
                        if (fadeIn) {
                            opacity += deltaTime / FADE_DURATION;
                            if (opacity >= 1) {
                                opacity = 1;
                                fadeIn = false;
                            }
                        }

                        // Start fade out near end of lifetime
                        if (!fadeOut && lifetime > maxLifetime - FADE_DURATION) {
                            fadeOut = true;
                        }

                        // Handle fade out
                        if (fadeOut) {
                            opacity -= deltaTime / FADE_DURATION;
                            if (opacity <= 0) {
                                opacity = 0;
                            }
                        }

                        // Update position (percentage of container)
                        x += velocityX * (deltaTime / 16.67); // Normalize to ~60fps
                        y += velocityY * (deltaTime / 16.67);

                        // Bounce off edges with some randomness
                        if (x < -10 || x > 110) {
                            velocityX = -velocityX * (0.8 + Math.random() * 0.4);
                            // Add slight random angle change
                            velocityY += (Math.random() - 0.5) * 0.5;
                        }
                        if (y < -10 || y > 110) {
                            velocityY = -velocityY * (0.8 + Math.random() * 0.4);
                            // Add slight random angle change
                            velocityX += (Math.random() - 0.5) * 0.5;
                        }

                        // Occasionally change direction randomly
                        if (Math.random() < 0.005) {
                            const angle = Math.random() * Math.PI * 2;
                            const speed = Math.sqrt(velocityX * velocityX + velocityY * velocityY);
                            velocityX = Math.cos(angle) * speed;
                            velocityY = Math.sin(angle) * speed;
                        }

                        // Update rotation
                        rotation += rotationSpeed;

                        return {
                            ...mascot,
                            x,
                            y,
                            velocityX,
                            velocityY,
                            rotation,
                            opacity,
                            fadeIn,
                            fadeOut,
                            lifetime,
                        };
                    })
                    // Remove mascots that have fully faded out
                    .filter((mascot) => mascot.opacity > 0 || mascot.fadeIn);

                return newMascots;
            });

            animationFrameRef.current = requestAnimationFrame(animate);
        };

        // Start with a few mascots immediately
        const initialMascots: Mascot[] = [];
        for (let i = 0; i < 5; i++) {
            initialMascots.push(createMascot());
        }
        setMascots(initialMascots);

        animationFrameRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [createMascot, updateContainerSize]);

    return (
        <div
            ref={containerRef}
            className="absolute inset-0 w-full h-full pointer-events-none z-[10] overflow-hidden"
        >
            {mascots.map((mascot) => {
                const pixelSize = getPixelSize(mascot.sizePercent);
                return (
                    <div
                        key={mascot.id}
                        className="absolute"
                        style={{
                            left: `${mascot.x}%`,
                            top: `${mascot.y}%`,
                            width: pixelSize,
                            height: pixelSize,
                            transform: `translate(-50%, -50%) rotate(${mascot.rotation}deg)`,
                            opacity: mascot.opacity,
                            transition: "opacity 0.1s ease-out",
                        }}
                    >
                        {/* Mascot image */}
                        <Image
                            src={mascot.image}
                            alt="Mascot"
                            width={pixelSize}
                            height={pixelSize}
                            className="drop-shadow-lg"
                            style={{
                                objectFit: "contain",
                            }}
                            priority
                        />
                    </div>
                );
            })}
        </div>
    );
};

export default MascotOverlay;
